import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";

const authHeader = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Quick chat launcher — small round button in the corner that expands to
 * reveal a grid of preset reactions. On click, sends { type: "quick_chat",
 * key } via the WebSocket `send`. Auto-collapses after selection.
 */
export default function QuickChat({ send, disabled, direction = "down" }) {
  const [open, setOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  const [cooldown, setCooldown] = useState(0);
  const rootRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/quick_chat", { headers: authHeader() }).then(r => {
      if (alive) setPresets(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!cooldown) return;
    const t = setTimeout(() => setCooldown(0), cooldown - Date.now());
    return () => clearTimeout(t);
  }, [cooldown]);

  const fire = (key) => {
    if (Date.now() < cooldown || disabled) return;
    send({ type: "quick_chat", key });
    setCooldown(Date.now() + 2000);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative inline-block" data-testid="quickchat-root">
      <button
        data-testid="quickchat-toggle"
        onClick={() => setOpen(o => !o)}
        disabled={disabled}
        className={`px-4 py-2.5 rounded-full text-sm sm:text-base font-semibold border-2 transition min-h-[44px] ${
          open
            ? "bg-white text-black border-white"
            : "bg-black/60 backdrop-blur text-white border-white/25 hover:bg-black/80"
        } disabled:opacity-40 disabled:cursor-not-allowed`}
        aria-label="Quick chat"
      >
        <span className="text-lg sm:text-xl">😀</span>
        <span className="ml-1.5 hidden xs:inline sm:inline">React</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: direction === "left" ? 12 : 0, y: direction === "up" ? -12 : direction === "down" ? 12 : 0, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: direction === "left" ? 12 : 0, y: direction === "up" ? -12 : direction === "down" ? 12 : 0, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
            className={`absolute z-50 w-64 sm:w-72 bg-[#161324] border border-white/10 rounded-2xl p-3 shadow-2xl ${
              direction === "up" ? "bottom-full right-0 mb-2"
              : direction === "left" ? "right-full top-1/2 -translate-y-1/2 mr-2"
              : "top-full right-0 mt-2"
            }`}
            data-testid="quickchat-popover"
          >
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2 font-semibold">React</div>
              <div className="grid grid-cols-3 gap-1.5">
                {presets.map((p, i) => (
                  <motion.button
                    key={p.key}
                    data-testid={`quickchat-${p.key}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.025 }}
                    onClick={() => fire(p.key)}
                    disabled={Date.now() < cooldown}
                    className="flex flex-col items-center gap-0.5 py-2 px-1 rounded-lg bg-white/5 hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="text-2xl leading-none">{p.emoji}</span>
                    <span className="text-[10px] text-white/70 font-semibold">{p.text}</span>
                  </motion.button>
                ))}
              </div>
              {cooldown > Date.now() && (
                <div className="text-[10px] text-center mt-2 text-white/40 uppercase tracking-widest">Cooldown…</div>
              )}
            </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/**
 * Renders a bouncing speech bubble anchored to the given wrapper element.
 * Show one per player at a time — pass `reaction` = {emoji, text} or null.
 */
export function ReactionBubble({ reaction }) {
  return (
    <AnimatePresence>
      {reaction && (
        <motion.div
          key={reaction.id}
          initial={{ opacity: 0, scale: 0.4, y: 10 }}
          animate={{ opacity: 1, scale: [0.6, 1.15, 1], y: [-2, -14, -8], rotate: [-3, 4, 0] }}
          exit={{ opacity: 0, y: -20, scale: 0.8 }}
          transition={{ duration: 0.45, ease: [0.2, 0.9, 0.25, 1.15] }}
          className="absolute -top-8 left-1/2 -translate-x-1/2 z-30 pointer-events-none"
          data-testid="reaction-bubble"
        >
          <div className="relative bg-white text-black rounded-2xl px-3 py-1.5 shadow-xl flex items-center gap-1.5 max-w-[220px] border-2 border-[color:var(--uno-yellow)]">
            <motion.span
              animate={{ rotate: [0, -18, 18, 0], scale: [1, 1.35, 1.1, 1] }}
              transition={{ duration: 0.75, ease: "easeOut" }}
              className="text-xl leading-none shrink-0"
            >{reaction.emoji}</motion.span>
            <span className="text-xs font-bold break-words leading-tight">{reaction.text}</span>
            {/* Tail */}
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rotate-45 bg-white border-b-2 border-r-2 border-[color:var(--uno-yellow)]" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Small hook: manages the {userId -> current reaction} map, listens for
 * `quick_reaction` websocket messages, and auto-clears each after `ttl`.
 *
 * Wire it by calling `pushReaction(reaction)` from the outer WS handler in
 * GameHub. Returns `[reactions, pushReaction]`.
 */
export function useReactions(ttl = 4000) {
  const [reactions, setReactions] = useState({}); // { userId: {id, emoji, text} }
  const pushReaction = useCallback((r) => {
    if (!r) return;
    const id = r.id || Math.random().toString(36).slice(2);
    setReactions(prev => ({ ...prev, [r.user_id]: { ...r, id } }));
    setTimeout(() => {
      setReactions(prev => {
        if (prev[r.user_id]?.id !== id) return prev;
        const next = { ...prev };
        delete next[r.user_id];
        return next;
      });
    }, ttl);
  }, [ttl]);
  return [reactions, pushReaction];
}
